import { useState, useEffect } from "react";
import "../styling/Account.css";
import { BsPlusCircleFill } from "react-icons/bs";
import { RiArrowUpSLine, RiArrowDownSLine } from "react-icons/ri";
import BookABoothRightPanel from "./panels/BookABoothRightPanel";
import axios from "axios";
import { useSelector } from "react-redux";
import { MdDelete } from "react-icons/md";

const BoothInfo = ({ onClickAccountInfoButton, initialSelected }) => {
  const [showOptions, setShowOptions] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const businessId = user?.businessId;
  const [selectedButton, setSelectedButton] = useState("booth");

  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);

  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [error, setError] = useState(null);
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [showAddOnForm, setShowAddOnForm] = useState(false);

  const [name, setName] = useState("");
  const [cost, setCost] = useState("");
  const [description, setDescription] = useState("");
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPackages = async () => {
    if (!businessId) { setLoadingPackages(false); setPackages([]); return; }
    try {
      setLoadingPackages(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/business/get-packages?businessId=${businessId}`);
      if (!response.ok) throw new Error("Failed to fetch packages");
      const data = await response.json();
      const activeItems = data.filter(item => item.status !== 'ARCHIVED') || [];
      activeItems.sort((a, b) => a.position - b.position);
      setPackages(activeItems);
    } catch (err) {
      console.error("Error fetching packages:", err); setError(err.message);
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchAddons = async () => {
    if (!businessId) { setLoadingAddons(false); setAddons([]); return; }
    try {
      setLoadingAddons(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/business/get-addon?businessId=${businessId}`);
      if (!response.ok) throw new Error("Failed to fetch add-ons");
      const data = await response.json();
      const activeItems = data.filter(item => item.status !== 'ARCHIVED') || [];
      activeItems.sort((a, b) => a.position - b.position);
      setAddons(activeItems);
    } catch (err) {
      console.error("Error fetching addons:", err); setError(err.message);
    } finally {
      setLoadingAddons(false);
    }
  };
  
  useEffect(() => {
    setSelectedButton(initialSelected === "account" || initialSelected === "company" ? initialSelected : "booth");
  }, [initialSelected]);

  const handleButtonClick = (buttonType) => {
    setSelectedButton(buttonType);
    if (onClickAccountInfoButton) onClickAccountInfoButton(buttonType);
  };

  useEffect(() => {
    if (businessId) {
      fetchPackages();
      fetchAddons();
    }
  }, [businessId]);

  const saveOrder = async (items, itemType) => {
    const endpoint = itemType === 'package' ? 'packages' : 'addons';
    const url = `${process.env.REACT_APP_API_URL}/business/${endpoint}/reorder`;
    const orderedIds = items.map(item => item._id);
    try {
      await axios.patch(url, { orderedIds }, { withCredentials: true });
    } catch (err) {
      console.error(`Failed to save ${itemType} order:`, err);
      alert('Could not save the new order. Please refresh and try again.');
    }
  };

  const handleSetPackage = async () => {
    if (!name || !cost || !description) { alert("Please fill in all fields."); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/business/set-packages`, { packages: [{ name, cost: Number(cost), description }] }, { withCredentials: true });
      await fetchPackages();
      setShowPackageForm(false);
      setName(""); setCost(""); setDescription("");
    } catch (error) {
      console.error("Error setting package:", error.response || error);
      alert(`Failed to create package. ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetAddOn = async () => {
    if (!name || !cost || !description) { alert("Please fill in all fields."); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/business/set-addon`, { packageAddon: [{ name, cost: Number(cost), description }] }, { withCredentials: true });
      await fetchAddons();
      setShowAddOnForm(false);
      setName(""); setCost(""); setDescription("");
    } catch (error) {
      console.error("Error setting addon:", error.response || error);
      alert(`Failed to create addon. ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId, itemType) => {
    if (!window.confirm(`Are you sure you want to permanently delete this ${itemType}?`)) return;
    const [list, setList] = itemType === "package" ? [packages, setPackages] : [addons, setAddons];
    const url = `${process.env.REACT_APP_API_URL}/business/${itemType === 'package' ? 'packages' : 'addons'}/${itemId}/archive`;
    try {
      await axios.patch(url, {}, { withCredentials: true });
      const remainingItems = list.filter((item) => item._id !== itemId);
      setList(remainingItems);
      await saveOrder(remainingItems, itemType);
      alert(`${itemType} deleted successfully.`);
    } catch (err) {
      console.error(`Error deleting ${itemType}:`, err);
      alert(`Failed to delete ${itemType}.`);
    }
  };

  const moveItem = (list, setList, index, direction, itemType) => {
    if ((direction === "up" && index === 0) || (direction === "down" && index === list.length - 1)) return;
    
    const newList = [...list];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    
    setList(newList);
    saveOrder(newList, itemType); 
  };

  const packagesAndAddonsList = (
    <div className="package-addOn-list">
      <h2 style={{ color: "#1C1154", marginTop: "0px", marginBottom: "30px" }}>Packages</h2>
      {packages.map((pkg, index) => (
        <div key={pkg?._id} className="right-panel-content" style={{ marginTop: "5px", padding: "20px", marginBottom: "15px" }}>
          <div className="right-panel-arrow-container">
            <span>
              <h1 style={{ color: '#000000', marginBottom: '0px' }}>${pkg?.cost}</h1>
              <h3 style={{ color: '#000000', marginTop: '0px', marginBottom: '5px', fontWeight: 400 }}>{pkg?.name}</h3>
            </span>
            <div style={{ display: "flex" }}>
              <RiArrowUpSLine
                onClick={() => moveItem(packages, setPackages, index, 'up', 'package')}
                style={{ cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#cccccc' : 'inherit' }}
              />
              <RiArrowDownSLine
                onClick={() => moveItem(packages, setPackages, index, 'down', 'package')}
                style={{ cursor: index === packages.length - 1 ? 'default' : 'pointer', color: index === packages.length - 1 ? '#cccccc' : 'inherit' }}
              />
              <MdDelete onClick={() => handleDeleteItem(pkg._id, 'package')} style={{ cursor: 'pointer' }} />
            </div>
          </div>
          <div style={{ color: "#4D4D4D", fontSize: "0.95em" }}>
            {(pkg?.description || "").split("\n").map((feature, i) => <p key={i} style={{ margin: "3px 0" }}>{feature}</p>)}
          </div>
        </div>
      ))}
      <h2 style={{ marginTop: "40px", marginBottom: "30px", color: "#1C1154" }}>Add Ons</h2>
      {addons.map((addon, index) => (
        <div key={addon?._id} className="bordered-box" style={{ display: "flex", alignItems: "center", gap: "15px", borderRadius: "8px", marginBottom: "15px", padding: "20px", border: "1px solid #e0e0e0" }}>
          <div className="content" style={{ flexGrow: 1 }}>
            <h1 style={{ color: '#000000' }}>${addon?.cost}</h1>
            <h3 style={{ color: "#000000", fontWeight: "400", marginTop: '0px', marginBottom: '10px' }}>{addon?.name}</h3>
            {(addon?.description || "").split("\n").map((line, i) => <p key={i} style={{ color: "#555", margin: "3px 0", fontSize: "0.9em" }}>{line}</p>)}
          </div>
          <div style={{ display: 'flex', alignSelf: 'baseline' }}>
            <RiArrowUpSLine
              onClick={() => moveItem(addons, setAddons, index, 'up', 'addon')}
              style={{ cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#cccccc' : 'inherit' }}
            />
            <RiArrowDownSLine
              onClick={() => moveItem(addons, setAddons, index, 'down', 'addon')}
              style={{ cursor: index === addons.length - 1 ? 'default' : 'pointer', color: index === addons.length - 1 ? '#cccccc' : 'inherit' }}
            />
            <MdDelete onClick={() => handleDeleteItem(addon._id, 'addon')} style= {{ cursor: 'pointer'}} />
          </div>
        </div>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: "1.75rem", marginTop: "30px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
        <div style={{ position: "relative" }}>
          <label onClick={() => setShowOptions(!showOptions)} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: "bold", color: "#000" }}>
            <BsPlusCircleFill size={22} style={{ color: "#2A1980" }} />
            Add New Item
          </label>
          {showOptions && (
            <div style={{ position: "absolute", bottom: "120%", left: 0, width: "280px", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "8px", padding: "16px", boxShadow: "0 4px 10px rgba(0,0,0,0.1)", zIndex: 100 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: "600" }}>Add New Item</h4>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div onClick={() => { setShowPackageForm(true); setShowOptions(false); }} style={{ border: "1px solid #ccc", borderRadius: "8px", width: "45%", padding: "10px", cursor: "pointer", textAlign: "center" }}>
                  <div style={{ marginBottom: "8px" }}><div style={{ height: "14px", backgroundColor: "#ccc", borderRadius: "4px", marginBottom: "6px" }} />{[1, 2, 3].map((_, i) => (<div key={i} style={{ height: "6px", backgroundColor: "#ddd", marginBottom: "4px", borderRadius: "3px" }} />))}</div><span style={{ color: "#555", fontWeight: "500" }}>Package</span>
                </div>
                <div onClick={() => { setShowAddOnForm(true); setShowOptions(false); }} style={{ border: "1px solid #ccc", borderRadius: "8px", width: "45%", padding: "10px", cursor: "pointer", textAlign: "center" }}>
                  <div style={{ marginBottom: "8px" }}>{[1, 2, 3].map((_, i) => (<div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px" }}><input type="checkbox" disabled /><div style={{ flexGrow: 1, height: "10px", backgroundColor: "#ddd", borderRadius: "4px" }} /></div>))}</div><span style={{ color: "#555", fontWeight: "500" }}>Add-On</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const modals = (
    <>
      {showPackageForm && (
        <div style={{ position: "fixed", top: 0, left: 0, height: "100vh", width: "100vw", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 200 }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "10px", padding: "24px", width: "320px" }}>
            <h3 style={{ marginBottom: "12px" }}>Create New Package</h3>
            <input type="text" placeholder="Package Name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px" }} />
            <input type="number" placeholder="Cost" value={cost} onChange={(e) => setCost(e.target.value)} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px" }} />
            <textarea placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px", resize: "none", fontFamily: "poppins" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button onClick={handleSetPackage} disabled={isSubmitting} style={{ backgroundColor: "#ffffff", color: "#080700", border: "1px solid #D9D9D9", padding: "10px 16px", borderRadius: "6px", cursor: isSubmitting ? "not-allowed" : "pointer", fontWeight: "bold", opacity: isSubmitting ? 0.6 : 1 }}>
                {isSubmitting ? "Saving..." : "Set Package"}
              </button>
              <button onClick={() => setShowPackageForm(false)} disabled={isSubmitting} style={{ backgroundColor: "#ffffff", color: "#080700", border: "1px solid #D9D9D9", padding: "10px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddOnForm && (
        <div style={{ position: "fixed", top: 0, left: 0, height: "100vh", width: "100vw", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 200 }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "10px", padding: "24px", width: "320px" }}>
            <h3 style={{ marginBottom: "12px" }}>Create New Add-On</h3>
            <input type="text" placeholder="Add-On Name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px" }} />
            <input type="number" placeholder="Cost" value={cost} onChange={(e) => setCost(e.target.value)} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px" }} />
            <textarea placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ width: "94%", padding: "10px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "6px", fontSize: "14px", resize: "none", fontFamily: "poppins" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button onClick={handleSetAddOn} disabled={isSubmitting} style={{ backgroundColor: "#ffffff", color: "#080700", border: "1px solid #d4d4d4", padding: "10px 16px", borderRadius: "6px", cursor: isSubmitting ? "not-allowed" : "pointer", fontWeight: "bold", opacity: isSubmitting ? 0.6 : 1 }}>
                {isSubmitting ? "Saving..." : "Set Add-On"}
              </button>
              <button onClick={() => setShowAddOnForm(false)} disabled={isSubmitting} style={{ backgroundColor: "#ffffff", color: "#080700", border: "1px solid #d4d4d4", padding: "10px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="booth-responsive-container" style={{ display: "flex", flexDirection: "row", minHeight: "100vh", marginBottom: "50px" }}>
        <div className="booth-responsive-main" style={{ width: "90%", paddingRight: "0px" }}>
          
          {/* HEADER SECTION ADJUSTED FOR ALIGNMENT */}
          <div className="accountInfo" style={{ 
            width: "100%",
            marginTop: "18px", 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
          }}>
            <h2 style={{ color: "#1C1154", fontWeight: "600", margin: 0 }}>Book-A-Booth</h2>
            
            <div className="accountButtons" style={{ display: "flex", gap: "10px" }}>
              <button 
                style={{ 
                  height: "40px", 
                  fontSize: "14px", 
                  padding: "0 15px", 
                  background: selectedButton === "account" ? "#2A1980" : "#FFFFFF", 
                  color: selectedButton === "account" ? "#FFFFFF" : "#1C1154", 
                  border: selectedButton === "account" ? "none" : "1px solid #dedede", 
                  borderRadius: "6px", 
                  cursor: "pointer", 
                  fontWeight: selectedButton === "account" ? "normal" : "normal" 
                }} 
                onClick={() => handleButtonClick("account")}
              >
                Account
              </button>
              <button 
                style={{ 
                  height: "40px", 
                  fontSize: "14px", 
                  padding: "0 15px", 
                  background: selectedButton === "company" ? "#2A1980" : "#FFFFFF", 
                  color: selectedButton === "company" ? "#FFFFFF" : "#1C1154", 
                  border: selectedButton === "company" ? "none" : "1px solid #dedede", 
                  borderRadius: "6px", 
                  cursor: "pointer", 
                  fontWeight: selectedButton === "company" ? "normal" : "normal" 
                }} 
                onClick={() => handleButtonClick("company")}
              >
                Company
              </button>
              <button 
                style={{ 
                  height: "40px", 
                  minWidth: "130px", 
                  fontSize: "14px", 
                  padding: "0 15px", 
                  background: selectedButton === "booth" ? "#2A1980" : "#FFFFFF", 
                  color: selectedButton === "booth" ? "#FFFFFF" : "#1C1154", 
                  border: selectedButton === "booth" ? "none" : "1px solid #dedede", 
                  borderRadius: "6px", 
                  cursor: "pointer", 
                  fontWeight: selectedButton === "booth" ? "normal" : "normal" 
                }} 
                onClick={() => handleButtonClick("booth")}
              >
                Book-A-Booth
              </button>
            </div>
          </div>

          {error && <div style={{ color: "red", padding: "20px", textAlign: "center" }}>Error: {error}</div>}
          {!businessId && !loadingPackages && !loadingAddons && <div style={{ padding: "30px", textAlign: "center", color: "grey", fontSize: "1.1em" }}>Business information not available. Please set up your business details first.</div>}
          {(loadingPackages || loadingAddons) && businessId && <div style={{ padding: "30px", textAlign: "center", fontSize: "1.1em" }}>Loading packages and add-ons...</div>}
          
          {businessId && !loadingPackages && !loadingAddons && !error && packagesAndAddonsList}
        </div>

        {/* Right Panel */}
        <div className="booth-responsive-sidebar" style={{ width: "25%", backgroundColor: "#f2f2f2", marginTop: "0px", position: "sticky", top: "20px", height: "fit-content" }}>
          <BookABoothRightPanel/>
        </div>
      </div>
      {modals}
    </>
  );
};

export default BoothInfo;